const CELL_SEPARATOR = ',';
const NEW_LINE_SEPARATOR = '\n';
const UNIVERSAL_BOM = '\uFEFF';
const CSV_PREFIX = `data:text/csv;charset=utf-8,${UNIVERSAL_BOM}`;

const CHARACTERS_TO_ESCAPE = [',', ' ', ';'];

/**
 * Wrap values containing unsafe characters (comma, space, semicolon) in double quotes so they can safely by used as CSV values
 */
export function sanitizeCSVValue(value: string) {
  const hasInvalidCharacter = CHARACTERS_TO_ESCAPE.some((char) => value?.toString().includes(char));
  // If the value contains invalid characters, enclose it in double quotes
  if (hasInvalidCharacter) {
    return `"${value}"`;
  }
  return value;
}

/**
 * Sanitize each value in the cvs row if possible, otherwise return the original row and returns a string of comma separated values
 * @param row An array of string or a string representing a row of CSV values
 * @returns A string of comma separated values
 */
function sanitizeCSVRow(row: Array<string> | string): string {
  if (Array.isArray(row)) {
    return row.map((cell) => sanitizeCSVValue(cell)).join(CELL_SEPARATOR);
  }
  return row;
}

/**
 * Sanitize each value in the cvs table if possible, otherwise return the original data
 * @param csvData An matrix of string or a string representing a grid of CSV values
 * @returns A string representing a csv table
 */
export function generateCSVString(csvData: string | ArrayLike<string> | ArrayLike<ArrayLike<string>>): string {
  if (csvData == null) {
    return '';
  }
  if (Array.isArray(csvData)) {
    const safeCSVRows: string[] = csvData.map((row) => sanitizeCSVRow(row));
    return safeCSVRows.join(NEW_LINE_SEPARATOR);
  }

  // If csv data is already a string, we cannot sanitize it, just return it
  return `${csvData}`;
}

/**
 * Generates a URI downloadable csv table
 */
export function generateCSVURI(csvData: string | ArrayLike<string> | ArrayLike<ArrayLike<string>>) {
  const safeCSVString = generateCSVString(csvData);
  return `${CSV_PREFIX}${encodeURIComponent(safeCSVString)}`;
}
